import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { extend, map } from 'underscore';
import classnames from 'classnames';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  IconButton,
  TextField,
  MessageBar,
  MessageBarType,
} from '@bingads-webui-react/fabric-control';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';

import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { StockImageFilter } from '@bingads-webui-campaign-react/stock-image-filter';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import { stockImageFilterLegacyI18nMap } from './legacy-i18n-map';
import { getFilterStyle, getSearchFieldStyle, getRootStyles } from './styles/index';

const PAGE_SIZE = 20;

export const StockImageView = ({
  getStockImages,
  onChangeImageIdSelection,
  enableMultipleSelection,
  imageCellWidth,
  imageCellHeight,
  images,
  setImages,
  hasMore,
  setHasMore,
  skip,
  setSkip,
  serializedFilterForComp,
  setSerializedFilterForComp,
  isLoadingFirstPage,
  setIsLoadingFirstPage,
  searchQuery,
  setSearchQuery,
  filter,
  setFilter,
}) => {
  const i18n = useContext(I18nContext);
  const { popoverContainer } = useDefaultStyles(getFilterStyle);
  const searchFieldClasses = useDefaultStyles(getSearchFieldStyle);
  const { stockImageRoot } = useDefaultStyles(getRootStyles);

  const filterI18n = useMemo(() => buildLegacyI18n(i18n, [stockImageFilterLegacyI18nMap]), [i18n]);

  const loadImages = useCallback(({ $search = searchQuery, $skip = skip, $query = filter }) => {
    if ($search) {
      if ($skip === 0) {
        setIsLoadingFirstPage(true);
      }

      getStockImages({
        customQueries: extend({
          appName: 'CampaignUI',
          $search,
          $skip,
          $top: PAGE_SIZE,
          language: 'en',
          includevertical: true,
        }, $query),
      }).then((data) => {
        setHasMore(data['@odata.count'] > data.value.length + $skip);

        setImages([
          ...($skip > 0 ? images : []),
          ...map(data.value, (item) => {
            const image = {
              thumbnailSrc: item.Preview.Url,
              id: item.Id,
              isSelected: false,
            };

            return image;
          }),
        ]);

        if ($skip === 0) {
          setIsLoadingFirstPage(false);
        }
      });
    }
  }, [
    images, setImages, getStockImages, searchQuery, skip, filter, setHasMore, setIsLoadingFirstPage,
  ]);

  const loadNextPage = useCallback(() => {
    if (hasMore) {
      const newSkip = skip + PAGE_SIZE;
      setSkip(newSkip);
      loadImages({ $skip: newSkip });
    }
  }, [skip, setSkip, loadImages, hasMore]);

  const onSelectImage = useCallback((index) => {
    const newArr = [...images];
    const selectedImage = newArr[index];

    const selectionChange = { selectedImageId: selectedImage.id };

    if (enableMultipleSelection) {
      const newSelectionState = !selectedImage.isSelected;

      newArr[index].isSelected = newSelectionState;
      selectionChange.selectionState = newSelectionState;
    }

    setImages(newArr);
    onChangeImageIdSelection(selectionChange);
  }, [images, setImages, onChangeImageIdSelection, enableMultipleSelection]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      setSkip(0);
      loadImages({
        $skip: 0,
      });
    }
  }, [setSkip, loadImages]);

  const onFilterQueryChange = useCallback((newFilterQuery) => {
    const newFilterQueryString = JSON.stringify(newFilterQuery);

    if (newFilterQueryString !== serializedFilterForComp) {
      setFilter(newFilterQuery);
      setSerializedFilterForComp(newFilterQueryString);
      setSkip(0);
      loadImages({
        $query: newFilterQuery,
        $skip: 0,
      });
    }
  }, [setFilter, setSerializedFilterForComp, loadImages, serializedFilterForComp, setSkip]);

  const messageBarStyles = useMemo(() => ({
    root: {
      marginBottom: 4,
      backgroundColor: '#EFF6FC',
    },
  }), []);

  // use a callback ref so that the filter popover container gets updated
  // reference to the popover container when it renders
  const [popoverContainerRef, setPopoverContainerRef] = useState(null);

  const popoverContainerCallbackRef =
    useCallback(node => setPopoverContainerRef(node), [setPopoverContainerRef]);

  return (
    <div className={classnames('stock-image-view', stockImageRoot)}>
      <div className={classnames(searchFieldClasses.container, 'stock-image-searchbar')}>
        <TextField
          value={searchQuery}
          onChange={(ev, newValue) => setSearchQuery(newValue)}
          onKeyDown={onKeyDown}
          placeholder={i18n.getString(_TL_('Search stock images (provided by Shutterstock)'))}
          styles={{
            root: {
              width: '50%',
              display: 'inline-block',
            },
          }}
        />
        <IconButton
          iconProps={{ iconName: 'Search' }}
          className="search-button"
          onClick={() => {
            setSkip(0);
            loadImages({
              $skip: 0,
            });
          }}
        />
      </div>
      <StockImageFilter
        i18nLegacy={filterI18n}
        onFilterQueryChange={onFilterQueryChange}
        popoverContainer={popoverContainerRef}
      />
      <div
        ref={popoverContainerCallbackRef}
        className={popoverContainer}
      />
      <MessageBar
        messageBarType={MessageBarType.info}
        styles={messageBarStyles}
      >
        {i18n.getString(_TL_('The Shutterstock watermark appearing on the stock images below will not appear in your ad.'))}
      </MessageBar>
      <MessageBar
        messageBarType={MessageBarType.info}
        styles={messageBarStyles}
      >
        {i18n.getString(_TL_('There may be images provided by Shutterstock that do not adhere to the Microsoft image policy.'))}
        <LearnMoreLink
          linkText={i18n.getString(_TL_('Learn more'))}
          topic="app54718"
        />
      </MessageBar>
      {
        isLoadingFirstPage ? <AssetLibrarySpinner useCustomSize /> :
        <FluentImagePicker
          className="stock-image-picker"
          images={images}
          pageSize={PAGE_SIZE}
          loadMoreImages={loadNextPage}
          onSelectImage={onSelectImage}
          hasMoreImages={hasMore}
          imageCellWidth={imageCellWidth}
          imageCellHeight={imageCellHeight}
        />
      }
    </div>
  );
};

StockImageView.propTypes = {
  getStockImages: PropTypes.func.isRequired,
  onChangeImageIdSelection: PropTypes.func.isRequired,
  enableMultipleSelection: PropTypes.bool,
  imageCellWidth: PropTypes.number,
  imageCellHeight: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setImages: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  setHasMore: PropTypes.func.isRequired,
  skip: PropTypes.number.isRequired,
  setSkip: PropTypes.func.isRequired,
  serializedFilterForComp: PropTypes.string.isRequired,
  setSerializedFilterForComp: PropTypes.func.isRequired,
  isLoadingFirstPage: PropTypes.bool.isRequired,
  setIsLoadingFirstPage: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  filter: PropTypes.shape({}).isRequired,
  setFilter: PropTypes.func.isRequired,
};

StockImageView.defaultProps = {
  enableMultipleSelection: true,
  imageCellWidth: undefined,
  imageCellHeight: undefined,
};
